import { AidStation, Segment, CalibrationProfile, CrewCheckIn, ETAWindow } from '../types'
import { calculateArrivalTime } from './calculations'

export interface CrewETACalculation {
  originalETA: number
  adjustedETA: number
  window: ETAWindow
  status: 'on-pace' | 'ahead' | 'behind'
  speedFactor?: number
}

export class CrewCalculations {
  static readonly DEFAULT_UNCERTAINTY_PERCENT = 0.03 // ±3%
  
  /**
   * Ensures there's always a finish line aid station at the total race distance
   */
  static ensureFinishLineStation(
    aidStations: AidStation[], 
    segments: Segment[]
  ): AidStation[] {
    if (segments.length === 0) return aidStations
    
    const totalDistance = segments.reduce((sum, s) => sum + s.distance_km, 0)
    
    // Check if there's already a station at or very close to the finish (within 0.1km)
    const hasFinishStation = aidStations.some(station => 
      Math.abs(station.at_distance_km - totalDistance) < 0.1
    )
    
    if (hasFinishStation) {
      return aidStations
    }
    
    // Create a finish line aid station
    const finishStation: AidStation = {
      id: `finish-${totalDistance.toFixed(1)}`,
      name: 'Finish Line',
      at_distance_km: totalDistance,
      planned_stop_min: 0,
      available: [],
      notes: 'Race finish - congratulations!',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      run_id: 'auto-generated'
    }
    
    return [...aidStations, finishStation].sort((a, b) => a.at_distance_km - b.at_distance_km)
  }
  
  static calculateETAWindow(
    baseETA: number, 
    uncertaintyPercent: number = CrewCalculations.DEFAULT_UNCERTAINTY_PERCENT
  ): ETAWindow {
    const uncertainty = baseETA * uncertaintyPercent
    const earliest = Math.max(0, baseETA - uncertainty)
    const latest = baseETA + uncertainty
    
    return {
      earliestMinutes: earliest,
      latestMinutes: latest,
      mostLikelyMinutes: baseETA,
      confidence: Math.max(0.5, 1 - uncertaintyPercent * 2) // Higher uncertainty = lower confidence
    }
  }

  static calculateAidStationETA(
    aidStation: AidStation,
    segments: Segment[],
    calibrationProfile?: CalibrationProfile | null,
    checkIns: CrewCheckIn[] = [],
    startTime?: Date,
    aidStations: AidStation[] = []
  ): CrewETACalculation {
    const originalETA = calculateArrivalTime(
      aidStation.at_distance_km,
      segments,
      calibrationProfile || undefined
    )

    // Find the most recent check-in before this aid station
    const relevantCheckIns = checkIns
      .filter(c => {
        const checkInStation = aidStations.find(s => s.id === c.aidStationId)
        return checkInStation && checkInStation.at_distance_km <= aidStation.at_distance_km
      })
      .sort((a, b) => b.actualTimeMinutes - a.actualTimeMinutes)

    const mostRecentCheckIn = relevantCheckIns[0]

    let adjustedETA = originalETA
    let speedFactor: number | undefined
    let uncertaintyPercent = CrewCalculations.DEFAULT_UNCERTAINTY_PERCENT

    if (mostRecentCheckIn) {
      const checkInStation = aidStations.find(s => s.id === mostRecentCheckIn.aidStationId)
      if (checkInStation) {
        const plannedTimeToCheckIn = calculateArrivalTime(
          checkInStation.at_distance_km,
          segments,
          calibrationProfile || undefined
        )
        
        speedFactor = mostRecentCheckIn.actualTimeMinutes / plannedTimeToCheckIn
        
        // Calculate time from check-in point to this aid station
        const timeFromCheckInToHere = calculateArrivalTime(
          aidStation.at_distance_km,
          segments,
          calibrationProfile || undefined
        ) - plannedTimeToCheckIn
        
        adjustedETA = mostRecentCheckIn.actualTimeMinutes + (timeFromCheckInToHere * speedFactor)
        
        // Adjust uncertainty based on how far we are from the check-in
        const distanceFromCheckIn = aidStation.at_distance_km - checkInStation.at_distance_km
        const baseUncertainty = CrewCalculations.DEFAULT_UNCERTAINTY_PERCENT
        
        // Uncertainty increases with distance from last check-in (0.5% per km, 12% max)
        uncertaintyPercent = Math.min(0.12, baseUncertainty + (distanceFromCheckIn * 0.005))
      }
    }

    const window = this.calculateETAWindow(adjustedETA, uncertaintyPercent)
    
    // Determine status based on speed factor
    let status: 'on-pace' | 'ahead' | 'behind' = 'on-pace'
    if (speedFactor !== undefined) {
      if (speedFactor < 0.95) {
        status = 'ahead'
      } else if (speedFactor > 1.05) {
        status = 'behind'
      }
    }

    return {
      originalETA,
      adjustedETA,
      window,
      status,
      speedFactor
    }
  }

  static calculateAllAidStationETAs(
    aidStations: AidStation[],
    segments: Segment[],
    calibrationProfile?: CalibrationProfile | null,
    checkIns: CrewCheckIn[] = []
  ): Map<string, CrewETACalculation> {
    const results = new Map<string, CrewETACalculation>()

    // Sort aid stations by distance
    const sortedStations = [...aidStations].sort((a, b) => a.at_distance_km - b.at_distance_km)

    for (const station of sortedStations) {
      const calculation = this.calculateAidStationETA(
        station,
        segments,
        calibrationProfile,
        checkIns,
        undefined,
        aidStations
      )
      results.set(station.id, calculation)
    }

    return results
  }

  static formatETAWindow(
    window: ETAWindow,
    startTime?: Date,
    showElapsed: boolean = false
  ): string {
    if (showElapsed || !startTime) {
      // Format as T+ elapsed time
      return `T+ ${this.formatElapsedTime(window.earliestMinutes)}–${this.formatElapsedTime(window.latestMinutes)}`
    } else {
      // Format as clock time
      const start = startTime.getTime()
      const earliest = new Date(start + window.earliestMinutes * 60 * 1000)
      const latest = new Date(start + window.latestMinutes * 60 * 1000)
      
      return `${this.formatClockTime(earliest)}–${this.formatClockTime(latest)}`
    }
  }

  static formatElapsedTime(minutes: number): string {
    const hours = Math.floor(minutes / 60)
    const mins = Math.floor(minutes % 60)
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}`
    }
    return `${mins}m`
  }

  static formatClockTime(date: Date): string {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false
    })
  }

  static parseTimeInput(input: string, method: 'clock' | 'elapsed'): number | null {
    if (method === 'clock') {
      // Parse HH:MM format
      const match = input.match(/^(\d{1,2}):(\d{2})$/)
      if (!match) return null
      
      const hours = parseInt(match[1], 10)
      const minutes = parseInt(match[2], 10)
      
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return null
      }
      
      // Convert to minutes since midnight
      return hours * 60 + minutes
    } else {
      // Parse MM:SS or HH:MM:SS format for elapsed time
      const parts = input.split(':').map(p => parseInt(p, 10))
      
      if (parts.length === 2) {
        // MM:SS format
        const [minutes, seconds] = parts
        if (minutes < 0 || seconds < 0 || seconds > 59) return null
        return minutes + seconds / 60
      } else if (parts.length === 3) {
        // HH:MM:SS format
        const [hours, minutes, seconds] = parts
        if (hours < 0 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
          return null
        }
        return hours * 60 + minutes + seconds / 60
      }
      
      return null
    }
  }

  static convertClockTimeToElapsed(clockTime: string, startTime: Date): number | null {
    const clockMinutes = this.parseTimeInput(clockTime, 'clock')
    if (clockMinutes === null) return null
    
    const today = new Date()
    const clockDateTime = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    clockDateTime.setHours(Math.floor(clockMinutes / 60), clockMinutes % 60, 0, 0)
    
    // Handle next day case
    if (clockDateTime < startTime) {
      clockDateTime.setDate(clockDateTime.getDate() + 1)
    }
    
    const elapsedMs = clockDateTime.getTime() - startTime.getTime()
    return elapsedMs / (1000 * 60) // Convert to minutes
  }

  static getNextAidStation(
    aidStations: AidStation[],
    checkIns: CrewCheckIn[]
  ): AidStation | null {
    if (aidStations.length === 0) return null
    
    const sortedStations = [...aidStations].sort((a, b) => a.at_distance_km - b.at_distance_km)
    
    // Find the first aid station without a check-in
    for (const station of sortedStations) {
      const hasCheckIn = checkIns.some(c => c.aidStationId === station.id)
      if (!hasCheckIn) {
        return station
      }
    }
    
    // All stations have check-ins, race is complete
    return null
  }

  static getRaceProgress(
    aidStations: AidStation[],
    segments: Segment[],
    checkIns: CrewCheckIn[]
  ): {
    completedDistance: number
    totalDistance: number
    progressPercent: number
    lastCheckInTime?: Date
  } {
    const totalDistance = segments.reduce((sum, s) => sum + s.distance_km, 0)
    
    if (checkIns.length === 0) {
      return {
        completedDistance: 0,
        totalDistance,
        progressPercent: 0
      }
    }
    
    // Find the check-in at the furthest distance
    let maxDistance = 0
    let lastCheckInTime: Date | undefined
    
    for (const checkIn of checkIns) {
      const station = aidStations.find(s => s.id === checkIn.aidStationId)
      if (station && station.at_distance_km > maxDistance) {
        maxDistance = station.at_distance_km
        lastCheckInTime = checkIn.timestamp
      }
    }
    
    return {
      completedDistance: maxDistance,
      totalDistance,
      progressPercent: (maxDistance / totalDistance) * 100,
      lastCheckInTime
    }
  }


  static getSpeedFactorColor(speedFactor: number): string {
    if (speedFactor < 0.95) {
      return 'text-green-600' // Ahead of pace
    } else if (speedFactor > 1.05) {
      return 'text-red-600' // Behind pace
    } else {
      return 'text-blue-600' // On pace
    }
  }

  static getStatusColor(status: 'on-pace' | 'ahead' | 'behind'): string {
    switch (status) {
      case 'ahead':
        return 'text-green-600'
      case 'behind':
        return 'text-red-600'
      case 'on-pace':
      default:
        return 'text-blue-600'
    }
  }

  static getStatusIcon(status: 'on-pace' | 'ahead' | 'behind'): string {
    switch (status) {
      case 'ahead':
        return '🏃‍♂️💨' // Running fast
      case 'behind':
        return '🐌' // Slow
      case 'on-pace':
      default:
        return '⏱️' // On time
    }
  }
}